import { UIElement } from '../ui-element.js';
import { element, trustedHTML } from '../../global/template-literals.js';
import { removeAttributes, setAttributes } from '../../global/ui-helpers.js';
import { addButtonLoader, toggleLoaderInButton } from '../../global/loader.js';
import { Component } from '../../decorators/component.decorator.js';
import { Attribute } from '../../decorators/attribute.decorator.js';
import { Child } from '../../decorators/child.decorator.js';
import styles from './ui-button.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIButton
 * @element ui-button
 * @classdesc Represents a class for <code>ui-button</code> element.
 * Wraps or creates a button according to the brand book.
 * Events (like `onClick`, `onChange` etc) and all generic HTML attributes (`aria-*`, `form*`) are being translated
 * straight to the generated button inside `ui-button` component.
 * In case of icon only button, do not forget to add `aria-label` attribute.
 * @property {string} [label] {@attr label} Button label.
 * @property {UIButtonLayout} [layout] {@attr layout} Button layout.
 * @property {string} glyph {@attr glyph} Icon glyph code.
 * @property {UIButtonIconPosition} iconPosition {@attr icon-position} Icon position (left or right).
 * @property {UIButtonType} [type] {@attr type} Button type.
 * @property {boolean} disabled {@attr disabled} Toggle disabled state of the button.
 * @property {boolean} loader {@attr loader} Toggle loader (spinner icon).
 * @property {HTMLButtonElement} button Read-only. Shortcut to child button element.
 * @property {HTMLElement} icon Read-only. Shortcut to child icon element.
 * @slot
 * @example
 * <ui-button label="Submit" glyph="add" icon-position="left"></ui-button>
 */
@Component({
    tag: 'ui-button',
    styles,
})
export class UIButton extends UIElement {
    /**
     * @type {UIButtonLayout}
     * @readonly
     */
    static readonly Layout = {
        Primary: 'primary',
        Secondary: 'secondary',
        Tertiary: 'tertiary',
    };

    /**
     * @type {UIButtonIconPosition}
     * @readonly
     */
    static readonly IconPosition = {
        Left: 'left',
        Right: 'right',
    };

    /**
     * @type {UIButtonType}
     * @readonly
     */
    static readonly Type = {
        Button: 'button',
        Submit: 'submit',
        Reset: 'reset',
    };

    /**
     * Attributes to be excluded from the button element.
     * @private
     */
    private excludedButtonAttributes = new Set<string>([
        'label',
        'icon-position',
        'glyph',
        'layout',
        'state',
        'class',
        'loader',
        'id',
    ]);

    @Attribute() label!: string;
    @Attribute() layout: string = UIButton.Layout.Primary;
    @Attribute() glyph!: string;
    @Attribute() iconPosition: string = UIButton.IconPosition.Right;
    @Attribute() type: string = UIButton.Type.Button;
    @Attribute() loader = false;
    @Attribute() disabled = false;
    @Child(':scope > button') button!: HTMLButtonElement;
    @Child(':scope > button > ui-icon') icon!: HTMLElement;

    /**
     * Contains initial HTML for the default slot (serving as an alternative to `label` prop)
     * @private
     */
    private slottedHTML!: string;

    /**
     * @type {string[]}
     */
    static get observedAttributes(): Array<string> {
        return ['disabled', 'loader', 'glyph'];
    }

    /**
     * @inheritDoc
     */
    observeAttributes(name: string, oldValue: string, newValue: string): void {
        if (oldValue === newValue || !this.hydrated) {
            return;
        }

        switch (name) {
            case 'loader':
                toggleLoaderInButton(this.button, this.loader, {
                    disabled: this.disabled,
                    loading: this.loader,
                });
                break;
            case 'disabled':
                this.button.disabled = this.disabled;
                break;
            case 'glyph':
                this.glyph = newValue;
                this.querySelector('ui-icon')?.setAttribute('glyph', newValue);
                break;
        }
    }

    /**
     * Fires click on the nested button. Serves as an API sugar allowing programmatic clicks on the host element.
     * @param {MouseEvent} event
     * @private
     */
    handleClick = (event: MouseEvent) => {
        if (this.button && event.target === this) {
            this.button.dispatchEvent(
                new MouseEvent('click', { ...event, bubbles: false })
            );
        }
    };

    /**
     * @private
     */
    subscribeEvents() {
        this.addEventListener('click', this.handleClick);
    }

    /**
     * @private
     */
    unsubscribeEvents() {
        this.removeEventListener('click', this.handleClick);
    }

    /**
     * @inheritDoc
     */
    hydrate() {
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    disconnect() {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    reconnect() {
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    render(): void {
        this.slottedHTML = this.innerHTML.trim();

        const slottedHTML = this.slottedHTML;
        this.innerHTML = '';

        // Get current attributes of the host, excluding attributes from the stop-list
        const genericButtonAttributes = Array.from(this.attributes)
            .filter(
                (attribute: Attr) =>
                    !this.excludedButtonAttributes.has(attribute.name)
            )
            .reduce<Record<string, string>>((collection, attribute: Attr) => {
                collection[attribute.name] = attribute.value;
                return collection;
            }, {});

        if (!this.label && !slottedHTML && this.glyph) {
            this.classList.add('-icon-only');
        }

        const button = element`
            <button type="${this.type}" class="ui-button__button">
                ${
                    this.label &&
                    !slottedHTML &&
                    element`<span class="ui-button__label">${this.label}</span> `
                }
                ${
                    !!slottedHTML &&
                    trustedHTML`
                    <span class="ui-button__label">${slottedHTML}</span>
                `
                }
                ${
                    this.glyph &&
                    element`<ui-icon glyph="${this.glyph}"></ui-icon>`
                }
            </button>
        ` as HTMLButtonElement;

        addButtonLoader(button, {
            loading: this.loader,
            disabled: this.disabled,
        });

        // Set current host attributes (excluding stop-list) on the button
        setAttributes(button, genericButtonAttributes);

        // Remove current attributes from the host (excluding stop-list)
        removeAttributes(
            this,
            Object.keys(genericButtonAttributes).filter(
                (name) => name !== 'disabled'
            )
        );

        this.append(button);
    }
}
